import type { LucideIcon } from "lucide-react-native";
import type { RequestBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";
import {
  Scissors,
  Syringe,
  Microscope,
  Target,
  ScanSearch,
  Zap,
  Shield,
  Ban,
  MoreHorizontal,
} from "lucide-react-native";

export type SurgeryType =
  | "tubal_ligation"
  | "salpingectomy"
  | "operative_laparoscopy"
  | "myomectomy"
  | "operative_hysteroscopy"
  | "varicocelectomy"
  | "orchiopexy"
  | "vasectomy"
  | "other";

export type SurgerySex = "AFAB" | "AMAB" | "both";

export interface SurgeryOption {
  id: SurgeryType;
  labelKey: string;
  icon: LucideIcon;
  sex: SurgerySex;
  /** CPT procedure code (US billing) */
  cpt?: string;
  /** SNOMED CT procedure concept ID */
  snomedCt?: string;
}

type PatchPayload = RequestBody<
  paths["/{tenantId}/patients/{patientId}"]["patch"]
>;
type MedicalHistoryPayload = NonNullable<PatchPayload["medicalHistory"]>;
type SurgeryPayload = NonNullable<MedicalHistoryPayload["surgeries"]>[number];
type SurgerySourceCode = NonNullable<SurgeryPayload["sourceCodes"]>[number];

const LABEL_KEY = (id: SurgeryType) =>
  `med_history.medical.surgeries.surgery_types.${id}`;

export const SURGERY_OPTIONS: SurgeryOption[] = [
  {
    id: "tubal_ligation",
    labelKey: LABEL_KEY("tubal_ligation"),
    icon: Ban,
    sex: "AFAB",
    cpt: "58600",
    snomedCt: "287664005",
  },
  {
    id: "salpingectomy",
    labelKey: LABEL_KEY("salpingectomy"),
    icon: Scissors,
    sex: "AFAB",
    cpt: "58700",
    snomedCt: "116143008",
  },
  {
    id: "operative_laparoscopy",
    labelKey: LABEL_KEY("operative_laparoscopy"),
    icon: Microscope,
    sex: "AFAB",
    cpt: "49320",
    snomedCt: "108191006",
  },
  {
    id: "myomectomy",
    labelKey: LABEL_KEY("myomectomy"),
    icon: Target,
    sex: "AFAB",
    cpt: "58140",
    snomedCt: "36582005",
  },
  {
    id: "operative_hysteroscopy",
    labelKey: LABEL_KEY("operative_hysteroscopy"),
    icon: ScanSearch,
    sex: "AFAB",
    cpt: "58558",
    snomedCt: "265632002",
  },
  {
    id: "varicocelectomy",
    labelKey: LABEL_KEY("varicocelectomy"),
    icon: Zap,
    sex: "AMAB",
    cpt: "55530",
    snomedCt: "71047002",
  },
  {
    id: "orchiopexy",
    labelKey: LABEL_KEY("orchiopexy"),
    icon: Shield,
    sex: "AMAB",
    cpt: "54640",
    snomedCt: "49368007",
  },
  {
    id: "vasectomy",
    labelKey: LABEL_KEY("vasectomy"),
    icon: Syringe,
    sex: "AMAB",
    cpt: "55250",
    snomedCt: "22523008",
  },
  {
    id: "other",
    labelKey: LABEL_KEY("other"),
    icon: MoreHorizontal,
    sex: "both",
  },
];

export function getSurgeryLabelKey(surgery: string): string {
  return (
    SURGERY_OPTIONS.find((o) => o.id === surgery)?.labelKey ??
    `med_history.medical.surgeries.surgery_types.other`
  );
}

/**
 * Return the terminology assertions authored with a curated intake option.
 * Custom/free-text surgery entries deliberately remain uncoded.
 */
export function getSurgerySourceCodes(
  surgery: string,
  display: string,
): SurgerySourceCode[] {
  const option = SURGERY_OPTIONS.find(({ id }) => id === surgery);
  if (!option) return [];

  const codes: SurgerySourceCode[] = [];
  if (option.cpt) {
    codes.push({ system: "CPT", code: option.cpt, display });
  }
  if (option.snomedCt) {
    codes.push({
      system: "SNOMED_CT_PROC",
      code: option.snomedCt,
      display,
    });
  }
  return codes;
}
